import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { X, ZoomIn, ZoomOut, Maximize2, Info, GitBranch } from 'lucide-react';

const NOTEBOOK_COLORS = [
  '#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#a855f7',
];
const ORPHAN_COLOR = '#6b7280';
const LINK_COLOR = 'rgba(255,255,255,0.08)';
const HIGHLIGHT_LINK = 'rgba(245,158,11,0.6)';

function extractLinks(content) {
  if (!content) return [];
  const text = typeof content === 'string' ? content : '';
  const regex = /\[\[([^\]]+)\]\]/g;
  const links = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    links.push(match[1]);
  }
  return links;
}

export default function GraphView({ onClose }) {
  const { notes, notebooks, setSelectedNoteId, setActiveView } = useApp();
  const graphRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [highlightNodes, setHighlightNodes] = useState(new Set());

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Build notebook color map
  const notebookColorMap = useMemo(() => {
    const map = {};
    notebooks.forEach((nb, i) => {
      map[nb.id] = NOTEBOOK_COLORS[i % NOTEBOOK_COLORS.length];
    });
    return map;
  }, [notebooks]);

  // Build graph data
  const graphData = useMemo(() => {
    const activeNotes = notes.filter((n) => !n.is_archived && !n.is_deleted);
    const titleToId = {};
    activeNotes.forEach((n) => {
      if (n.title) titleToId[n.title.toLowerCase()] = n.id;
    });

    const nodes = activeNotes.map((n) => ({
      id: n.id,
      name: n.title || 'Untitled',
      color: n.notebook_id ? (notebookColorMap[n.notebook_id] || ORPHAN_COLOR) : ORPHAN_COLOR,
      notebook_id: n.notebook_id,
      is_pinned: n.is_pinned,
      is_favorited: n.is_favorited,
      tags: n.tags || [],
      val: 1,
    }));

    const nodeIds = new Set(activeNotes.map((n) => n.id));
    const links = [];
    const linkSet = new Set();

    activeNotes.forEach((note) => {
      const linkedNames = extractLinks(note.content);
      linkedNames.forEach((name) => {
        const targetId = titleToId[name.toLowerCase()];
        if (targetId && targetId !== note.id && nodeIds.has(targetId)) {
          const key = [note.id, targetId].sort().join('->');
          if (!linkSet.has(key)) {
            linkSet.add(key);
            links.push({ source: note.id, target: targetId });
          }
        }
      });
    });

    return { nodes, links };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, notebookColorMap]);

  // Stats
  const stats = useMemo(() => {
    const connectedIds = new Set();
    graphData.links.forEach((l) => {
      connectedIds.add(typeof l.source === 'object' ? l.source.id : l.source);
      connectedIds.add(typeof l.target === 'object' ? l.target.id : l.target);
    });
    return {
      total: graphData.nodes.length,
      connections: graphData.links.length,
      orphans: graphData.nodes.length - connectedIds.size,
    };
  }, [graphData]);

  // Highlight neighbors on hover
  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node || null);
    const newHighlightLinks = new Set();
    const newHighlightNodes = new Set();
    if (node) {
      newHighlightNodes.add(node.id);
      graphData.links.forEach((link) => {
        const srcId = typeof link.source === 'object' ? link.source.id : link.source;
        const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
        if (srcId === node.id || tgtId === node.id) {
          newHighlightLinks.add(link);
          newHighlightNodes.add(srcId);
          newHighlightNodes.add(tgtId);
        }
      });
    }
    setHighlightLinks(newHighlightLinks);
    setHighlightNodes(newHighlightNodes);
  }, [graphData.links]);

  // Navigate to note on click
  const handleNodeClick = useCallback((node) => {
    setSelectedNoteId(node.id);
    setActiveView('all');
    onClose();
  }, [setSelectedNoteId, setActiveView, onClose]);

  // Custom node rendering on canvas
  const paintNode = useCallback((node, ctx, globalScale) => {
    const isHighlighted = highlightNodes.size === 0 || highlightNodes.has(node.id);
    const isHovered = hoveredNode?.id === node.id;
    const fontSize = Math.max(10 / globalScale, 2);
    const radius = isHovered ? 7 : 5;
    const alpha = isHighlighted ? 1 : 0.15;

    // Glow for hovered node
    if (isHovered) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius + 4, 0, 2 * Math.PI);
      ctx.fillStyle = `${node.color}33`;
      ctx.fill();
    }

    // Node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = node.color;
    ctx.globalAlpha = alpha;
    ctx.fill();
    ctx.globalAlpha = 1;

    // Label (only show when zoomed in enough or hovered)
    if (globalScale > 1.5 || isHovered) {
      ctx.font = `${isHovered ? 'bold ' : ''}${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isHighlighted ? '#e5e7eb' : '#6b728066';
      ctx.fillText(node.name, node.x, node.y + radius + 2);
    }
  }, [highlightNodes, hoveredNode]);

  // Custom link rendering
  const paintLink = useCallback((link, ctx) => {
    const isHighlighted = highlightLinks.has(link);
    ctx.strokeStyle = isHighlighted ? HIGHLIGHT_LINK : LINK_COLOR;
    ctx.lineWidth = isHighlighted ? 1.5 : 0.5;
    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.stroke();
  }, [highlightLinks]);

  // Zoom controls
  const zoomIn = () => graphRef.current?.zoom(graphRef.current.zoom() * 1.4, 300);
  const zoomOut = () => graphRef.current?.zoom(graphRef.current.zoom() / 1.4, 300);
  const zoomFit = () => graphRef.current?.zoomToFit(400, 60);

  // Fit on mount
  useEffect(() => {
    const timer = setTimeout(() => graphRef.current?.zoomToFit(600, 80), 500);
    return () => clearTimeout(timer);
  }, []);

  // Notebook legend (only notebooks that have notes in graph)
  const legendItems = useMemo(() => {
    const usedNotebooks = new Set(graphData.nodes.map((n) => n.notebook_id).filter(Boolean));
    const items = notebooks
      .filter((nb) => usedNotebooks.has(nb.id))
      .map((nb) => ({ name: nb.name, color: notebookColorMap[nb.id] }));
    const hasOrphans = graphData.nodes.some((n) => !n.notebook_id);
    if (hasOrphans) items.push({ name: 'No notebook', color: ORPHAN_COLOR });
    return items;
  }, [graphData.nodes, notebooks, notebookColorMap]);

  return (
    <div className="flex flex-col h-full w-full bg-background" data-testid="graph-view">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-secondary/30">
        <div className="flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-accent-amber" />
          <h2 className="text-base font-semibold tracking-tight">Note Graph</h2>
          <div className="hidden sm:flex items-center gap-3 ml-3 text-xs text-muted-foreground">
            <span>{stats.total} notes</span>
            <span className="text-border">|</span>
            <span>{stats.connections} links</span>
            <span className="text-border">|</span>
            <span>{stats.orphans} orphans</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={zoomIn} title="Zoom in" data-testid="graph-zoom-in">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={zoomOut} title="Zoom out" data-testid="graph-zoom-out">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={zoomFit} title="Fit view" data-testid="graph-zoom-fit">
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 ml-1" onClick={onClose} title="Close graph" data-testid="graph-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Graph canvas */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        {graphData.nodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <GitBranch className="w-12 h-12 opacity-30" />
            <p className="text-sm">No notes to visualize</p>
            <p className="text-xs opacity-60">Create some notes and link them with [[note name]] syntax</p>
          </div>
        ) : (
          <ForceGraph2D
            ref={graphRef}
            graphData={graphData}
            width={dimensions.width}
            height={dimensions.height}
            backgroundColor="transparent"
            nodeCanvasObject={paintNode}
            nodePointerAreaPaint={(node, color, ctx) => {
              ctx.beginPath();
              ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            }}
            linkCanvasObject={paintLink}
            onNodeHover={handleNodeHover}
            onNodeClick={handleNodeClick}
            cooldownTicks={120}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
            enableNodeDrag
            enableZoomInteraction
            enablePanInteraction
          />
        )}

        {/* Legend overlay */}
        {legendItems.length > 0 && (
          <div className="absolute bottom-4 left-4 bg-secondary/80 backdrop-blur-sm border border-border/50 rounded-lg p-3 max-w-[200px]" data-testid="graph-legend">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 font-medium">Notebooks</p>
            <div className="space-y-1.5">
              {legendItems.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-foreground/80 truncate">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hover tooltip */}
        {hoveredNode && (
          <div className="absolute top-4 right-4 bg-secondary/90 backdrop-blur-sm border border-border/50 rounded-lg p-3 max-w-[240px]" data-testid="graph-tooltip">
            <p className="text-sm font-medium text-foreground truncate">{hoveredNode.name}</p>
            {hoveredNode.notebook_id && (
              <p className="text-xs text-muted-foreground mt-1">
                {notebooks.find((nb) => nb.id === hoveredNode.notebook_id)?.name || 'Unknown'}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground/60 mt-1.5">Click to open</p>
          </div>
        )}

        {/* Mobile stats */}
        <div className="sm:hidden absolute top-4 left-4 bg-secondary/80 backdrop-blur-sm border border-border/50 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="w-3 h-3" />
            <span>{stats.total} notes</span>
            <span>{stats.connections} links</span>
          </div>
        </div>
      </div>
    </div>
  );
}
